import { createContext, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import { PersonaProfile } from '@/modules/operational/modules/integration/domain/models/PersonaProfiles'
import { PersonaStatus } from '@/shared/services/api/operators/get-clinic-operators'

const personaFilterContext = createContext<{
  filter: {
    personaName: string
    size: number
    accessProfile: PersonaProfile | undefined
    personaStatus: PersonaStatus | undefined
  }
  rawInputs: {
    personaName: string
  }
  setPersonaName: (value: string) => void
  setSize: React.Dispatch<React.SetStateAction<number>>
  setAccessProfile: React.Dispatch<
    React.SetStateAction<PersonaProfile | undefined>
  >
  setPersonaStatus: React.Dispatch<
    React.SetStateAction<PersonaStatus | undefined>
  >
}>({
  filter: {
    personaName: '',
    size: 20,
    accessProfile: undefined,
    personaStatus: undefined
  },
  rawInputs: {
    personaName: ''
  },
  setPersonaName: () => {},
  setSize: () => {},
  setAccessProfile: () => {},
  setPersonaStatus: () => {}
})

export const usePersonaFilterContext = () => useContext(personaFilterContext)

const DEBOUNCE_DELAY = 500

export function PersonaFilterProvider({
  children
}: {
  children: React.ReactNode
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [size, setSize] = useState<number>(20)
  const [accessProfile, setAccessProfile] = useState<
    PersonaProfile | undefined
  >(undefined)
  const [personaStatus, setPersonaStatus] = useState<PersonaStatus | undefined>(
    undefined
  )
  const [rawPersonaName, setRawPersonaName] = useState<string>(
    searchParams.get('personaName') || ''
  )

  useEffect(() => {
    setRawPersonaName(searchParams.get('personaName') || '')
  }, [searchParams])

  const handleSetPersonaName = (value: string) => {
    setRawPersonaName(value)
    if (value) {
      searchParams.set('personaName', value)
    } else {
      searchParams.delete('personaName')
    }
    setSearchParams(searchParams, { replace: true })
  }

  const [debouncedPersonaName] = useDebounceValue(
    rawPersonaName,
    DEBOUNCE_DELAY
  )

  return (
    <personaFilterContext.Provider
      value={{
        filter: {
          personaName: debouncedPersonaName,
          size,
          accessProfile,
          personaStatus
        },
        rawInputs: {
          personaName: rawPersonaName
        },
        setPersonaName: handleSetPersonaName,
        setSize,
        setAccessProfile,
        setPersonaStatus
      }}
    >
      {children}
    </personaFilterContext.Provider>
  )
}
